import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ManagementApi } from '../api/client';
import { RefreshActionButton } from '../components/RefreshActionButton';
import { PropertyTable, ResourceHeader, StatusStrip, Tabs } from '../components/ResourceDetail';
import { useI18n, useVisibleCopy, type LocaleCode } from '../i18n';
import {
  buildRuntimeAuditDetailModel,
  buildRuntimeEngagementDetailModel,
  buildRuntimeEvidenceModel,
  buildRuntimeProfileEvidenceModel,
  buildRuntimeSessionDetailModel,
  eventRow,
  runtimeContractGaps,
  type RuntimeActionStatus,
  type RuntimeAuditRow,
  type RuntimeEngagementDetailModel,
  type RuntimeEngagementDetailSources,
  type RuntimeEngagementRow,
  type RuntimeEventRow,
  type RuntimeEvidenceModel,
  type RuntimeEvidenceSources,
  type RuntimeHistoryRow,
  type RuntimeLoopVerdictSummary,
  type RuntimePromptArtifactRow,
  type RuntimeRelationshipGroup,
  type RuntimeSessionDetailModel,
  type RuntimeSessionDetailSources,
  type RuntimeSessionRow,
  type RuntimeStatusTone,
} from './model';

type RuntimeListState = {
  loading: boolean;
  sources: RuntimeEvidenceSources;
  model: RuntimeEvidenceModel;
  errors: Record<string, string>;
};

type EngagementState = {
  loading: boolean;
  sources: RuntimeEngagementDetailSources;
  model: RuntimeEngagementDetailModel;
  errors: Record<string, string>;
};

type SessionState = {
  loading: boolean;
  sources: RuntimeSessionDetailSources;
  model: RuntimeSessionDetailModel;
  errors: Record<string, string>;
};

type RuntimeTab = 'engagements' | 'sessions' | 'audit' | 'gaps';
type EngagementTab = 'overview' | 'timeline' | 'stream' | 'controls' | 'exports' | 'test-message' | 'raw';
type SessionTab = 'overview' | 'history' | 'raw';
type ProfileTab = 'overview' | 'prompts' | 'raw';
type AuditTab = 'overview' | 'raw';
type RuntimeActionKey = 'auditHistory' | 'runtimeOperations' | 'runtimeProfile' | 'sessionDetail' | 'runtimeAudit';

type EngagementFilters = { personaId: string; platform: string; state: string };

type ControlDraft = {
  action: string;
  operatorId: string;
  reason: string;
  confirmation: string;
  preview?: unknown;
  result?: string;
  error?: string;
};

type TestMessageDraft = {
  sessionId: string;
  message: string;
  confirmation: string;
  previewed: boolean;
  result?: string;
  error?: string;
};

const runtimeTabs: Array<{ id: RuntimeTab; label: string }> = [
  { id: 'engagements', label: 'Engagements' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'audit', label: 'Audit' },
  { id: 'gaps', label: 'Contract gaps' },
];

const engagementTabs: Array<{ id: EngagementTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'stream', label: 'SSE stream' },
  { id: 'controls', label: 'Controls' },
  { id: 'exports', label: 'Exports' },
  { id: 'test-message', label: 'Test message' },
  { id: 'raw', label: 'JSON' },
];

const sessionTabs: Array<{ id: SessionTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'history', label: 'Messages / history' },
  { id: 'raw', label: 'JSON' },
];

const profileTabs: Array<{ id: ProfileTab; label: string }> = [
  { id: 'overview', label: 'Evidence' },
  { id: 'prompts', label: 'Prompt refs' },
  { id: 'raw', label: 'JSON' },
];

const auditTabs: Array<{ id: AuditTab; label: string }> = [
  { id: 'overview', label: 'Audit rows' },
  { id: 'raw', label: 'JSON' },
];

export function RuntimeEvidencePage({ initialTab }: { initialTab?: string | null }) {
  const { locale } = useI18n();
  const ui = useVisibleCopy();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<RuntimeTab>(() => normalizeRuntimeTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [filters, setFilters] = useState<EngagementFilters>({ personaId: '', platform: '', state: '' });
  const [state, setState] = useState<RuntimeListState>(() => {
    const sources: RuntimeEvidenceSources = {};
    return { loading: true, sources, model: buildRuntimeEvidenceModel(sources), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeRuntimeTab(initialTab)), [initialTab]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const [engagements, sessions, audit] = await Promise.all([
        capture('engagements', api.getEngagements({ limit: '50', personaId: filters.personaId, platform: filters.platform, state: filters.state }), errors),
        capture('sessions', api.getSessions({ limit: '50' }), errors),
        capture('audit', api.getAudit({ limit: '50' }), errors),
      ]);
      if (!mounted) return;
      const sources = { engagements, sessions, audit, errors };
      setState({ loading: false, sources, model: buildRuntimeEvidenceModel(sources), errors });
    }
    load();
    return () => { mounted = false; };
  }, [api, filters.personaId, filters.platform, filters.state, reloadToken]);

  const { model } = state;

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow="Runtime"
        title={model.header.title}
        description={model.header.description}
        meta={model.header.meta}
        actions={<div className="action-row"><a className="btn btn-subtle" href="#/runtime/audit">{runtimeActionLabel(locale, 'auditHistory')}</a><RefreshActionButton loading={state.loading} onClick={() => setReloadToken(value => value + 1)} /></div>}
      />
      <StatusStrip items={model.status} />
      <RuntimeNotice loading={state.loading} notices={model.partialNotices} />
      {model.emptyState ? <EmptyState state={model.emptyState} /> : null}
      <Tabs tabs={runtimeTabs} activeId={activeTab} onSelect={tab => setActiveTab(normalizeRuntimeTab(tab))} />

      {activeTab === 'engagements' ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{ui('Engagement Operations')}</h2>
              <p>{ui('Filters, health/state badges, and links into state machine, live stream, exports, controls, and test-message guards.')}</p>
            </div>
            <EngagementFilterBar filters={filters} onChange={setFilters} />
          </div>
          <RuntimeEngagementTable engagements={model.engagements} emptyState={model.emptyState} />
        </section>
      ) : activeTab === 'sessions' ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{ui('Sessions')}</h2>
              <p>{ui('Messages/history, runtime profile, provider/model/thread, and prompt composition evidence.')}</p>
            </div>
          </div>
          <RuntimeSessionTable sessions={model.sessions} emptyState={model.emptyState} />
        </section>
      ) : activeTab === 'audit' ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{ui('Runtime Audit History')}</h2>
              <p>{ui('Audit rows are readable first; JSON details are available from an explicit tab.')}</p>
            </div>
          </div>
          <RuntimeAuditTable rows={model.auditRows} />
        </section>
      ) : (
        <ContractGaps gaps={model.contractGaps} />
      )}
    </div>
  );
}

export function RuntimeEngagementDetailPage({ engagementId, initialTab }: { engagementId: string; initialTab?: string | null }) {
  const { locale } = useI18n();
  const ui = useVisibleCopy();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<EngagementTab>(() => normalizeEngagementTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<EngagementState>(() => {
    const sources: RuntimeEngagementDetailSources = {};
    return { loading: true, sources, model: buildRuntimeEngagementDetailModel(sources), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeEngagementTab(initialTab)), [initialTab]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const [engagement, relations, engagementState, events, flow, loopVerdict] = await Promise.all([
        capture('engagement detail', api.getEngagement(engagementId), errors),
        capture('relations', api.getEngagementRelations(engagementId), errors),
        capture('state machine projection', api.getEngagementState(engagementId), errors),
        capture('timeline', api.getEvents({ engagementId, limit: '100' }), errors),
        capture('state machine flow', api.getStateMachineFlow('engagement'), errors),
        captureOptional(api.getLoopVerdict({ engagementId })),
      ]);
      if (!mounted) return;
      const sources = { engagement, relations, state: engagementState, events, flow, loopVerdict, errors };
      setState({ loading: false, sources, model: buildRuntimeEngagementDetailModel(sources), errors });
    }
    load();
    return () => { mounted = false; };
  }, [api, engagementId, reloadToken]);

  const { model } = state;

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow="Runtime engagement"
        title={model.engagement.id || engagementId}
        description="Operational center for relationships, state machine projection, event timeline/SSE, guarded controls, exports, and controlled test messages."
        meta={[
          ['Persona', model.engagement.personaName || 'Unknown'],
          ['Platform', model.engagement.platform || 'Unknown'],
          ['Session key', model.engagement.sessionKey || 'Unknown'],
          ['State', model.engagement.state || 'Unknown'],
        ]}
        actions={<div className="action-row"><a className="btn btn-subtle" href="#/runtime">{runtimeActionLabel(locale, 'runtimeOperations')}</a><RefreshActionButton loading={state.loading} onClick={() => setReloadToken(value => value + 1)} /></div>}
      />
      <StatusStrip items={model.status} />
      <RuntimeNotice loading={state.loading} notices={model.partialNotices} />
      <Tabs tabs={engagementTabs} activeId={activeTab} onSelect={tab => setActiveTab(normalizeEngagementTab(tab))} />

      {activeTab === 'overview' ? (
        <>
          <section className="panel">
            <div className="panel-header"><h2>{ui('Loop Verdict')}</h2><p>{ui('Contract, mechanics, Sentinel, release-owner, and overall gate status.')}</p></div>
            <LoopVerdictCard verdict={model.loopVerdict} />
          </section>
          <section className="panel">
            <div className="panel-header"><h2>{ui('Relationship Cards')}</h2><p>{ui('Explicit parent/child/sibling links from EngagementControls metadata.')}</p></div>
            <RelationshipCards groups={model.relationshipGroups} />
          </section>
          <section className="panel">
            <div className="panel-header"><h2>{ui('State Machine')}</h2><p>{ui('Projection, axes, and action availability from backend contracts.')}</p></div>
            <div className="content-grid">
              <PropertyTable rows={model.axesRows.length ? model.axesRows : [['Axes', 'Contract gap: axes unavailable']]} />
              <PropertyTable rows={model.stateMachine} />
            </div>
          </section>
          <ContractGaps gaps={model.contractGaps} compact />
        </>
      ) : activeTab === 'timeline' ? (
        <section className="panel">
          <div className="panel-header"><h2>{ui('Event Timeline')}</h2><p>{ui('Structured payload/artifact summary first; JSON opens only from each event card.')}</p></div>
          <EventTimeline events={model.timeline} />
        </section>
      ) : activeTab === 'stream' ? (
        <RuntimeLiveStream api={api} engagementId={model.engagement.id || engagementId} />
      ) : activeTab === 'controls' ? (
        <RuntimeControlPanel api={api} engagementId={model.engagement.id || engagementId} actions={[...model.allowedActions, ...model.blockedActions]} />
      ) : activeTab === 'exports' ? (
        <RuntimeExports api={api} engagementId={model.engagement.id || engagementId} />
      ) : activeTab === 'test-message' ? (
        <RuntimeTestMessageTool api={api} engagement={model.engagement} sessions={getEngagementSessions(state.sources.engagement)} />
      ) : (
        <section className="panel"><pre className="json-preview">{model.rawJson}</pre></section>
      )}
    </div>
  );
}

export function RuntimeSessionDetailPage({ sessionId, initialTab }: { sessionId: string; initialTab?: string | null }) {
  const { locale } = useI18n();
  const ui = useVisibleCopy();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<SessionTab>(() => normalizeSessionTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<SessionState>(() => {
    const sources: RuntimeSessionDetailSources = {};
    return { loading: true, sources, model: buildRuntimeSessionDetailModel(sources), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeSessionTab(initialTab)), [initialTab]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const session = await capture('session detail', api.getSession(sessionId), errors);
      if (!mounted) return;
      const sources = { session, errors };
      setState({ loading: false, sources, model: buildRuntimeSessionDetailModel(sources), errors });
    }
    load();
    return () => { mounted = false; };
  }, [api, sessionId, reloadToken]);

  const { model } = state;

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow="Runtime session"
        title={model.session.name}
        description="Session messages/history, runtime profile, provider/model/thread, and prompt composition evidence inside the token-protected console."
        meta={[
          ['Session', model.session.id || sessionId],
          ['Persona', model.session.personaName || 'Unknown'],
          ['Platform', model.session.platform || 'Unknown'],
          ['Runtime status', model.session.runtimeStatus],
        ]}
        actions={<div className="action-row"><a className="btn btn-subtle" href={model.session.profileHref}>{runtimeActionLabel(locale, 'runtimeProfile')}</a><RefreshActionButton loading={state.loading} onClick={() => setReloadToken(value => value + 1)} /></div>}
      />
      <StatusStrip items={sessionStatus(model.session)} />
      <RuntimeNotice loading={state.loading} notices={model.partialNotices} />
      <Tabs tabs={sessionTabs} activeId={activeTab} onSelect={tab => setActiveTab(normalizeSessionTab(tab))} />

      {activeTab === 'overview' ? (
        <>
          <section className="panel">
            <div className="panel-header"><h2>{ui('Runtime Relationships')}</h2></div>
            <PropertyTable rows={model.relationships} />
          </section>
          <section className="panel">
            <div className="panel-header"><h2>{ui('Prompt Composition Evidence')}</h2><p>{ui('Token-protected Console v2 may show prompt/module evidence; credential-like fields stay redacted.')}</p></div>
            <PromptModuleEvidence modules={model.promptModules} />
          </section>
        </>
      ) : activeTab === 'history' ? (
        <section className="panel">
          <div className="panel-header"><h2>{ui('Messages / History')}</h2><p>{ui('Message content is visible in Console v2; secret/token-like substrings are still redacted.')}</p></div>
          <RuntimeHistory rows={model.historyRows} />
        </section>
      ) : (
        <section className="panel"><pre className="json-preview">{model.rawJson}</pre></section>
      )}
    </div>
  );
}

export function RuntimeProfileEvidencePage({ sessionId, initialTab }: { sessionId: string; initialTab?: string | null }) {
  const { locale } = useI18n();
  const ui = useVisibleCopy();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<ProfileTab>(() => normalizeProfileTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<SessionState>(() => {
    const sources: RuntimeSessionDetailSources = {};
    return { loading: true, sources, model: buildRuntimeSessionDetailModel(sources), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeProfileTab(initialTab)), [initialTab]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const session = await capture('session detail', api.getSession(sessionId), errors);
      if (!mounted) return;
      const sources = { session, errors };
      setState({ loading: false, sources, model: buildRuntimeSessionDetailModel(sources), errors });
    }
    load();
    return () => { mounted = false; };
  }, [api, sessionId, reloadToken]);

  const profile = buildRuntimeProfileEvidenceModel(state.sources);

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow="Runtime profile evidence"
        title={profile.profileId || 'Missing SessionRuntimeProfile'}
        description="Runtime profile, provider/model/thread, and prompt references from the session contract."
        meta={[
          ['Session', profile.session.id || sessionId],
          ['Status', profile.session.runtimeStatus],
          ['Provider/model', profile.session.providerModel],
          ['Prompt refs', String(profile.promptArtifacts.length)],
        ]}
        actions={<div className="action-row"><a className="btn btn-subtle" href={profile.session.detailHref}>{runtimeActionLabel(locale, 'sessionDetail')}</a><RefreshActionButton loading={state.loading} onClick={() => setReloadToken(value => value + 1)} /></div>}
      />
      <StatusStrip items={sessionStatus(profile.session)} />
      <RuntimeNotice loading={state.loading} notices={profile.partialNotices} />
      {profile.emptyState ? <EmptyState state={profile.emptyState} /> : null}
      <Tabs tabs={profileTabs} activeId={activeTab} onSelect={tab => setActiveTab(normalizeProfileTab(tab))} />

      {activeTab === 'overview' ? (
        <section className="panel">
          <div className="panel-header"><h2>{ui('Evidence relationships')}</h2></div>
          <PropertyTable rows={profile.relationships} />
        </section>
      ) : activeTab === 'prompts' ? (
        <section className="panel">
          <div className="panel-header"><h2>{ui('Prompt artifact refs')}</h2><p>{ui('Only artifact ids, versions, and hashes are shown.')}</p></div>
          <PromptArtifactTable rows={profile.promptArtifacts} />
        </section>
      ) : (
        <section className="panel"><pre className="json-preview">{profile.rawJson}</pre></section>
      )}
    </div>
  );
}

export function RuntimeAuditHistoryPage({ eventId, initialTab }: { eventId?: string; initialTab?: string | null }) {
  const { locale } = useI18n();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<AuditTab>(() => normalizeAuditTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<RuntimeListState>(() => {
    const sources: RuntimeEvidenceSources = {};
    return { loading: true, sources, model: buildRuntimeEvidenceModel(sources), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeAuditTab(initialTab)), [initialTab]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const audit = await capture('audit', api.getAudit({ limit: '100' }), errors);
      if (!mounted) return;
      const sources = { audit, errors };
      setState({ loading: false, sources, model: buildRuntimeEvidenceModel(sources), errors });
    }
    load();
    return () => { mounted = false; };
  }, [api, reloadToken]);

  const detail = eventId ? buildRuntimeAuditDetailModel({ eventId, audit: state.sources.audit, errors: state.errors }) : null;

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow="Runtime audit"
        title={detail?.event ? detail.event.action : eventId ? 'Audit event' : 'Audit History'}
        description="Runtime/control-plane audit history. Credential-like fields stay redacted until reveal-token support exists in the contract."
        meta={[
          ['Rows', String(state.model.auditRows.length)],
          ['Source', '/api/v1/audit'],
          ['JSON detail', 'Explicit tab'],
          ['Detail lookup', eventId ? 'Current page' : 'List'],
        ]}
        actions={<div className="action-row"><a className="btn btn-subtle" href="#/runtime?tab=audit">{runtimeActionLabel(locale, 'runtimeAudit')}</a><RefreshActionButton loading={state.loading} onClick={() => setReloadToken(value => value + 1)} /></div>}
      />
      <RuntimeNotice loading={state.loading} notices={state.model.partialNotices} />
      {detail?.emptyState ? <EmptyState state={detail.emptyState} /> : null}
      <Tabs tabs={auditTabs} activeId={activeTab} onSelect={tab => setActiveTab(normalizeAuditTab(tab))} />
      {activeTab === 'overview' ? (
        detail?.event ? <AuditDetail event={detail.event} /> : <RuntimeAuditTable rows={state.model.auditRows} />
      ) : (
        <section className="panel"><pre className="json-preview">{detail ? detail.rawJson : state.model.rawJson}</pre></section>
      )}
    </div>
  );
}

function EngagementFilterBar({ filters, onChange }: { filters: EngagementFilters; onChange: (filters: EngagementFilters) => void }) {
  const ui = useVisibleCopy();

  return (
    <div className="action-row runtime-filter-row">
      <label className="field-label">{ui('Persona')}<input value={filters.personaId} placeholder="wizard" onChange={event => onChange({ ...filters, personaId: event.target.value })} /></label>
      <label className="field-label">{ui('Platform')}<input value={filters.platform} placeholder="linear" onChange={event => onChange({ ...filters, platform: event.target.value })} /></label>
      <label className="field-label">{ui('State')}<input value={filters.state} placeholder="coding-in-progress" onChange={event => onChange({ ...filters, state: event.target.value })} /></label>
      <button className="btn" type="button" onClick={() => onChange({ personaId: '', platform: '', state: '' })}>{ui('Clear')}</button>
    </div>
  );
}

function RuntimeEngagementTable({ engagements, emptyState }: { engagements: RuntimeEngagementRow[]; emptyState: RuntimeEvidenceModel['emptyState'] }) {
  const ui = useVisibleCopy();

  if (!engagements.length) return emptyState ? <EmptyState state={emptyState} /> : <div className="empty-state"><strong>{ui('No engagements match the filters.')}</strong><p>{ui('Adjust filters or wait for runtime activity.')}</p></div>;
  return (
    <div className="table-scroll">
      <table className="data-table runtime-table">
        <thead>
          <tr>
            <th>{ui('Engagement')}</th>
            <th>{ui('Health / state')}</th>
            <th>{ui('Sessions')}</th>
            <th>{ui('Updated')}</th>
          </tr>
        </thead>
        <tbody>
          {engagements.map(engagement => (
            <tr key={engagement.id} onClick={() => { window.location.hash = engagement.detailHref.replace(/^#/, ''); }}>
              <td data-label={ui('Engagement')}><strong>{engagement.id}</strong><br /><small>{engagement.personaName || ui('unknown')} · {engagement.platform || ui('unknown')} · {engagement.sessionKey || ui('no session key')}</small></td>
              <td data-label={ui('Health / state')}><StateBadge tone={engagement.health.tone} label={engagement.health.label} /> <span className="muted-inline">{ui(engagement.state || 'Unknown')}</span></td>
              <td data-label={ui('Sessions')}>{engagement.sessionsCount}<br /><small>{ui(`${engagement.liveSessions} live`)}</small></td>
              <td data-label={ui('Updated')}>{engagement.updatedAt || ui('unknown')}<br /><small>{ui(`Created ${engagement.createdAt || 'unknown'}`)}</small></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RuntimeSessionTable({ sessions, emptyState }: { sessions: RuntimeSessionRow[]; emptyState: RuntimeEvidenceModel['emptyState'] }) {
  const ui = useVisibleCopy();

  if (!sessions.length) return emptyState ? <EmptyState state={emptyState} /> : null;
  return (
    <div className="table-scroll">
      <table className="data-table runtime-table">
        <thead>
          <tr>
            <th>{ui('Session')}</th>
            <th>{ui('Runtime profile')}</th>
            <th>{ui('Provider/model')}</th>
            <th>{ui('Thread/status')}</th>
            <th>{ui('Prompt evidence')}</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map(session => (
            <tr key={session.id} onClick={() => { window.location.hash = session.detailHref.replace(/^#/, ''); }}>
              <td data-label={ui('Session')}><strong>{session.name}</strong><br /><small>{session.personaName || ui('unknown')} · {session.platform || ui('unknown')} · {session.sessionKey || ui('no session key')}</small></td>
              <td data-label={ui('Runtime profile')}>{session.runtimeProfileId || ui('Missing')}<br /><small>{session.personaSnapshotId || ui('no persona snapshot')}</small></td>
              <td data-label={ui('Provider/model')}>{session.providerModel}</td>
              <td data-label={ui('Thread/status')}>{session.threadId || ui('Unavailable')}<br /><small>{session.runtimeStatus}</small></td>
              {/* An absent hash is only a gap when the adapter has a
                  developer-instructions channel; otherwise it is the fact that
                  it has none, and promptEvidenceNote says which. */}
              <td data-label={ui('Prompt evidence')}>{session.developerInstructionsHash || session.developerPromptArtifact || ui(session.promptEvidenceNote || 'Missing')}<br /><small>{ui(`${session.promptArtifactCount} artifact refs`)}{session.promptInjectionStrategy ? ` · ${session.promptInjectionStrategy}` : ''}</small></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RelationshipCards({ groups }: { groups: RuntimeRelationshipGroup[] }) {
  const ui = useVisibleCopy();

  return (
    <div className="runtime-card-grid">
      {groups.map(group => (
        <div className="runtime-card" key={group.label}>
          <h3>{ui(group.label)}</h3>
          {group.rows.length ? group.rows.map(row => (
            <a className="runtime-related-card" href={row.detailHref} key={`${group.label}:${row.id}`}>
              <strong>{row.id}</strong>
              <small>{ui(row.relation || group.label.toLowerCase())} · {row.personaName || ui('unknown')} · {ui(row.state || 'unknown state')}</small>
            </a>
          )) : <p>{ui(noRelationshipRowsMessage(group.label))}</p>}
        </div>
      ))}
    </div>
  );
}

function noRelationshipRowsMessage(label: string) {
  if (label === 'Parents') return 'No parent engagements recorded.';
  if (label === 'Children') return 'No child engagements recorded.';
  if (label === 'Siblings') return 'No sibling engagements recorded.';
  return `No ${label.toLowerCase()} recorded.`;
}

function LoopVerdictCard({ verdict }: { verdict: RuntimeLoopVerdictSummary }) {
  const ui = useVisibleCopy();
  const [openJson, setOpenJson] = useState(false);
  if (!verdict.available) {
    return <div className="empty-state"><strong>{ui('No Loop Verdict returned.')}</strong><p>{ui('Create or query a workflow contract for this engagement to populate gate status.')}</p></div>;
  }
  return (
    <div className="page-stack compact-stack">
      <div className="runtime-card-grid">
        <div className="runtime-card">
          <h3>{ui('Overall')}</h3>
          <StateBadge tone={verdict.overallTone} label={verdict.overall || ui('Unavailable')} />
          <small>{verdict.workflowId || ui('unknown workflow')} · {ui('round')} {verdict.currentRound || '—'}</small>
        </div>
        {verdict.gates.map(gate => (
          <div className="runtime-card" key={gate.label}>
            <h3>{ui(gate.label)}</h3>
            <StateBadge tone={gate.tone} label={gate.status || ui('Unavailable')} />
            {gate.eventId ? <small>{gate.eventId}</small> : gate.reason ? <small>{gate.reason}</small> : null}
          </div>
        ))}
      </div>
      <PropertyTable rows={[
        ['Issue', verdict.issueId || '—'],
        ['Missing events', listText(verdict.missingEvents)],
        ['Invalid events', listText(verdict.invalidEvents)],
        ['Fallback used', verdict.fallbackUsed ? 'Yes' : 'No'],
        ['Manual mirror', verdict.manualMirrorDetected ? 'Detected' : 'No'],
        ['Next action', verdict.nextAction || '—'],
      ]} />
      <div className="action-row">
        <button className="btn btn-compact btn-subtle" type="button" onClick={() => setOpenJson(value => !value)}>{ui(openJson ? 'Hide JSON' : 'View JSON')}</button>
      </div>
      {openJson ? <pre className="json-preview">{verdict.rawJson}</pre> : null}
    </div>
  );
}

function listText(items: string[]) {
  return items.length ? items.join(', ') : 'None';
}

function EventTimeline({ events }: { events: RuntimeEventRow[] }) {
  const ui = useVisibleCopy();
  const [openJson, setOpenJson] = useState<string | null>(null);
  if (!events.length) return <div className="empty-state"><strong>{ui('No events returned.')}</strong><p>{ui('The timeline appears when canonical runtime events exist for this engagement.')}</p></div>;
  const sections = timelineSections(events);
  return (
    <div className="record-list timeline-list">
      {sections.map(section => (
        <div className="timeline-workflow-group" key={section.key}>
          <div className="timeline-workflow-header">
            <strong>{section.label}</strong>
            <small>{section.meta}</small>
          </div>
          {section.events.map(event => (
            <div className="record-card timeline-card" key={`${section.key}:${event.id}`}>
              <div className="timeline-card-header">
                <div>
                  <strong>{event.type || ui('unknown event')}</strong>
                  <small>{event.timestamp || ui('unknown time')} · {event.actor || ui('unknown actor')}</small>
                </div>
                <button className="btn btn-compact" type="button" onClick={() => setOpenJson(openJson === event.id ? null : event.id)}>{ui(openJson === event.id ? 'Hide JSON' : 'View JSON')}</button>
              </div>
              <PropertyTable rows={[
                ['Event ID', event.id],
                ['Workflow', event.workflowId || '—'],
                ['Round', event.round || '—'],
                ['From → To', [event.fromPersona || event.actor || 'unknown', event.toPersona || '—'].join(' → ')],
                ['Intent', event.intent || event.type || '—'],
                ['Result', event.result || 'pending'],
                ['Parent event', event.parentEventId || '—'],
                ['State change', event.stateChange],
                ['Persona', event.personaId || 'unknown'],
                ['Session', event.sessionId || '—'],
              ]} />
              {event.payloadFields.length ? <PayloadFieldList fields={event.payloadFields} /> : <p>{ui('No structured payload fields.')}</p>}
              {event.artifactRefs.length ? <ArtifactList artifacts={event.artifactRefs} /> : null}
              {openJson === event.id ? <pre className="json-preview">{event.rawJson}</pre> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function timelineSections(events: RuntimeEventRow[]) {
  const sections = new Map<string, { key: string; label: string; meta: string; events: RuntimeEventRow[] }>();
  for (const event of events) {
    const workflowId = event.workflowId || 'unscoped';
    const round = event.round || 'unknown';
    const key = `${workflowId}:${round}`;
    const fromTo = [event.fromPersona || event.actor || 'unknown', event.toPersona || '—'].join(' → ');
    const meta = [fromTo, event.intent || event.type, event.result || 'pending'].filter(Boolean).join(' · ');
    if (!sections.has(key)) {
      sections.set(key, {
        key,
        label: event.workflowId ? `Workflow ${event.workflowId} · round ${round}` : 'Ungrouped runtime events',
        meta,
        events: [],
      });
    }
    sections.get(key)?.events.push(event);
  }
  return [...sections.values()];
}

function PayloadFieldList({ fields }: { fields: RuntimeEventRow['payloadFields'] }) {
  return (
    <dl className="record-meta payload-fields">
      {fields.map(field => (
        <div key={field.key}>
          <dt>{field.key}</dt>
          <dd>{field.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ArtifactList({ artifacts }: { artifacts: RuntimeEventRow['artifactRefs'] }) {
  const ui = useVisibleCopy();
  return <div className="artifact-list">{artifacts.map((artifact, index) => <span className="badge" key={`${artifact.kind}:${artifact.ref}:${index}`}>{artifact.kind || ui('artifact')} · {artifact.ref || ui('missing ref')}</span>)}</div>;
}

function RuntimeLiveStream({ api, engagementId }: { api: ManagementApi; engagementId: string }) {
  const ui = useVisibleCopy();
  const [events, setEvents] = useState<RuntimeEventRow[]>([]);
  const [status, setStatus] = useState('Connecting to /api/v1/events/stream…');

  useEffect(() => {
    const url = api.getEventsStreamUrl({ engagementId });
    const source = new EventSource(url);
    source.onopen = () => setStatus('Connected: live SSE stream is open.');
    source.onmessage = event => {
      setEvents(previous => [eventRow(JSON.parse(event.data)), ...previous].slice(0, 20));
    };
    source.addEventListener('ping', () => setStatus(`Heartbeat ${new Date().toLocaleTimeString()}`));
    source.onerror = () => setStatus('SSE stream disconnected or unavailable.');
    return () => source.close();
  }, [api, engagementId]);

  return (
    <section className="panel">
      <div className="panel-header"><h2>{ui('SSE / Live Event Stream')}</h2><p>{ui(status)}</p></div>
      <EventTimeline events={events} />
    </section>
  );
}

function RuntimeControlPanel({ api, engagementId, actions }: { api: ManagementApi; engagementId: string; actions: RuntimeActionStatus[] }) {
  const ui = useVisibleCopy();
  const [draft, setDraft] = useState<ControlDraft>({ action: 'pause', operatorId: 'console-v2', reason: '', confirmation: '' });
  const selected = actions.find(action => action.action === draft.action);
  const confirmationText = `${draft.action} ${engagementId}`;
  const canSubmit = Boolean(previewAllowsControl(draft.preview, draft.action) && draft.reason.trim() && draft.operatorId.trim() && draft.confirmation === confirmationText);

  const update = (patch: Partial<ControlDraft>) => setDraft(previous => ({ ...previous, ...patch, result: undefined, error: undefined }));

  const preview = async () => {
    try {
      const nextPreview = await api.previewEngagementTransition(engagementId, {
        action: draft.action,
        operator_id: draft.operatorId,
        reason: draft.reason,
        confirmation: draft.confirmation,
      });
      setDraft(previous => ({ ...previous, preview: nextPreview, result: undefined, error: undefined }));
    } catch (error) {
      setDraft(previous => ({ ...previous, error: error instanceof Error ? error.message : String(error) }));
    }
  };

  const submit = async () => {
    if (!previewAllowsControl(draft.preview, draft.action)) {
      setDraft(previous => ({ ...previous, error: 'Latest transition preview does not allow this action.' }));
      return;
    }
    try {
      await api.postEngagementControl(engagementId, draft.action, { reason: draft.reason }, draft.operatorId);
      setDraft(previous => ({ ...previous, result: `${draft.action} sent successfully. Refreshing projection may take one reload.`, error: undefined }));
    } catch (error) {
      setDraft(previous => ({ ...previous, error: error instanceof Error ? error.message : String(error) }));
    }
  };

  return (
    <section className="panel">
      <div className="panel-header"><h2>{ui('Guarded Controls')}</h2><p>{ui('Preview, reason, and explicit typed confirmation are required in Console v2. Skip is submitted only when backend preview allows it.')}</p></div>
      <div className="form-grid">
        <label className="field-label">{ui('Action')}
          <select value={draft.action} onChange={event => update({ action: event.target.value, preview: undefined, confirmation: '' })}>
            {['pause', 'resume', 'skip'].map(action => <option key={action} value={action}>{ui(action)}</option>)}
          </select>
        </label>
        <label className="field-label">{ui('Operator ID')}<input value={draft.operatorId} onChange={event => update({ operatorId: event.target.value, preview: undefined })} /></label>
        <label className="field-label form-span">{ui('Reason')}<textarea value={draft.reason} onChange={event => update({ reason: event.target.value, preview: undefined })} placeholder={ui('Required operational reason')} /></label>
        <label className="field-label form-span">{ui('Explicit confirmation')}<input value={draft.confirmation} onChange={event => update({ confirmation: event.target.value })} placeholder={confirmationText} /></label>
      </div>
      {selected ? <ActionPreviewCard action={selected} /> : null}
      <div className="action-row">
        <button className="btn" type="button" onClick={preview}>{ui('Preview target transition')}</button>
        <button className="btn btn-primary" type="button" disabled={!canSubmit} onClick={submit}>{ui('Confirm and send')} {ui(draft.action)}</button>
      </div>
      {draft.preview ? <pre className="json-preview">{JSON.stringify(draft.preview, null, 2)}</pre> : null}
      {draft.result ? <div className="notice"><strong>{ui(draft.result)}</strong></div> : null}
      {draft.error ? <div className="notice notice-warning"><strong>{ui('Control failed.')}</strong> {ui(draft.error)}</div> : null}
      <div className="notice notice-warning"><strong>{ui('Contract gap:')}</strong> {ui('backend pause/resume/skip endpoints do not enforce explicit confirmation; this UI guard is intentionally stricter.')}</div>
    </section>
  );
}

export function previewAllowsControl(preview: unknown, action: string) {
  const record = preview && typeof preview === 'object' && !Array.isArray(preview) ? preview as Record<string, unknown> : {};
  const data = record.data && typeof record.data === 'object' && !Array.isArray(record.data) ? record.data as Record<string, unknown> : record;
  return data.allowed === true && data.action === action;
}

function ActionPreviewCard({ action }: { action: RuntimeActionStatus }) {
  const ui = useVisibleCopy();

  return (
    <div className={action.allowed ? 'notice' : 'notice notice-warning'}>
      <strong>{ui(action.action)}: {ui(action.allowed ? 'Allowed by backend preview' : 'Blocked by backend preview')}</strong>
      <p>{ui(action.message || action.code || `Current ${action.currentState || 'unknown'} → target ${action.targetState || 'unknown'}`)}</p>
      <p>{ui('Required')}: {action.requiredFields.join(', ') || ui('none')} · {ui('Records event')}: {ui(action.wouldRecordEvent ? 'yes' : 'no')}</p>
    </div>
  );
}

function RuntimeExports({ api, engagementId }: { api: ManagementApi; engagementId: string }) {
  const ui = useVisibleCopy();
  const [status, setStatus] = useState('');

  const download = async (format: 'ndjson' | 'json' | 'csv') => {
    try {
      const { blob, fileName } = await api.downloadEventsExport({ format, engagementId });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
      setStatus(`Export started: ${fileName}`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <section className="panel">
      <div className="panel-header"><h2>{ui('Event Exports')}</h2><p>{ui('Downloads are filtered to this engagement via /api/v1/events/export.')}</p></div>
      <div className="action-row">
        <button className="btn" type="button" onClick={() => download('ndjson')}>{ui('Export NDJSON')}</button>
        <button className="btn" type="button" onClick={() => download('json')}>{ui('Export JSON')}</button>
        <button className="btn" type="button" onClick={() => download('csv')}>{ui('Export CSV')}</button>
      </div>
      <PropertyTable rows={[["engagement_id", engagementId], ['Formats', 'ndjson, json, csv']]} />
      {status ? <div className="notice">{ui(status)}</div> : null}
    </section>
  );
}

function RuntimeTestMessageTool({ api, engagement, sessions }: { api: ManagementApi; engagement: RuntimeEngagementRow; sessions: RuntimeSessionRow[] }) {
  const ui = useVisibleCopy();
  const defaultSession = sessions[0];
  const [draft, setDraft] = useState<TestMessageDraft>({ sessionId: defaultSession?.id ?? '', message: '', confirmation: '', previewed: false });
  const selectedSession = sessions.find(session => session.id === draft.sessionId) ?? defaultSession;
  const sessionKey = selectedSession?.sessionKey || engagement.sessionKey;
  const confirmationText = `send test to ${engagement.personaName}:${sessionKey}`;
  const canSend = Boolean(draft.previewed && draft.message.trim() && draft.confirmation === confirmationText && engagement.personaName && sessionKey);

  const update = (patch: Partial<TestMessageDraft>) => setDraft(previous => ({ ...previous, ...patch, result: undefined, error: undefined }));

  const submit = async () => {
    try {
      await api.sendPersonaTestMessage(engagement.personaName, { session_key: sessionKey, message: draft.message });
      setDraft(previous => ({ ...previous, result: 'Test message sent.', error: undefined }));
    } catch (error) {
      setDraft(previous => ({ ...previous, error: error instanceof Error ? error.message : String(error) }));
    }
  };

  return (
    <section className="panel">
      <div className="panel-header"><h2>{ui('Controlled Test Message')}</h2><p>{ui('Target preview comes first, then explicit confirmation before /personas/{persona}/send.')}</p></div>
      <div className="notice notice-warning"><strong>{ui('Guardrail:')}</strong> {ui('this is a controlled operational tool. It cannot send until target preview is acknowledged and confirmation text matches exactly.')}</div>
      <div className="form-grid">
        <label className="field-label">{ui('Target session')}
          <select value={draft.sessionId} onChange={event => update({ sessionId: event.target.value, previewed: false, confirmation: '' })}>
            {sessions.map(session => <option key={session.id} value={session.id}>{session.name || session.id}</option>)}
            {!sessions.length ? <option value="">{ui('Contract gap: no session rows')}</option> : null}
          </select>
        </label>
        <label className="field-label">{ui('Confirmation')}<input value={draft.confirmation} onChange={event => update({ confirmation: event.target.value })} placeholder={confirmationText} /></label>
        <label className="field-label form-span">{ui('Message')}<textarea value={draft.message} onChange={event => update({ message: event.target.value, previewed: false })} placeholder={ui('Message to send to the selected runtime target')} /></label>
      </div>
      <section className="runtime-card">
        <h3>{ui('Target Preview')}</h3>
        <PropertyTable rows={[
          ['Persona', engagement.personaName || 'Unknown'],
          ['Session key', sessionKey || 'Missing'],
          ['Platform', selectedSession?.platform || engagement.platform || 'Unknown'],
          ['Message preview', draft.message || 'No message entered'],
        ]} />
        <button className="btn" type="button" disabled={!draft.message.trim() || !engagement.personaName || !sessionKey} onClick={() => update({ previewed: true })}>{ui('Acknowledge target preview')}</button>
      </section>
      <div className="action-row">
        <button className="btn btn-primary" type="button" disabled={!canSend} onClick={submit}>{ui('Confirm and send test message')}</button>
      </div>
      {draft.result ? <div className="notice"><strong>{ui(draft.result)}</strong></div> : null}
      {draft.error ? <div className="notice notice-warning"><strong>{ui('Send failed.')}</strong> {ui(draft.error)}</div> : null}
      <div className="notice notice-warning"><strong>{ui('Contract gap:')}</strong> {ui('backend send endpoint has no dedicated Runtime preview/audit contract; Console v2 performs a local target preview guard.')}</div>
    </section>
  );
}

function RuntimeAuditTable({ rows }: { rows: RuntimeAuditRow[] }) {
  const ui = useVisibleCopy();

  if (!rows.length) return <div className="empty-state"><strong>{ui('No audit rows returned.')}</strong><p>{ui('Audit history appears when the control plane returns events.')}</p></div>;
  return (
    <div className="table-scroll">
      <table className="data-table runtime-table">
        <thead>
          <tr>
            <th>{ui('Time')}</th>
            <th>{ui('Action')}</th>
            <th>{ui('Actor')}</th>
            <th>{ui('Resource')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id} onClick={() => { window.location.hash = row.detailHref.replace(/^#/, ''); }}>
              <td data-label={ui('Time')}>{row.timestamp || ui('unknown time')}<br /><small>{row.id}</small></td>
              <td data-label={ui('Action')}><strong>{row.action}</strong></td>
              <td data-label={ui('Actor')}>{row.actor}</td>
              <td data-label={ui('Resource')}>{row.resourceLabel}<br /><small>{row.resourceId}</small></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PromptModuleEvidence({ modules }: { modules: Array<Record<string, unknown>> }) {
  const ui = useVisibleCopy();

  if (!modules.length) return <div className="empty-state"><strong>{ui('No prompt module rows returned.')}</strong><p>{ui('The session API did not include effective prompt module evidence.')}</p></div>;
  return (
    <div className="table-scroll">
      <table className="data-table runtime-table">
        <thead>
          <tr><th>{ui('Name')}</th><th>{ui('Artifact')}</th><th>{ui('Version')}</th><th>{ui('Enabled')}</th></tr>
        </thead>
        <tbody>
          {modules.map((module, index) => (
            <tr key={`${String(module.name ?? 'module')}:${String(module.artifact_id ?? 'artifact')}:${index}`}>
              <td data-label={ui('Name')}>{String(module.display_name ?? module.name ?? ui('unnamed'))}</td>
              <td data-label={ui('Artifact')}>{String(module.artifact_id ?? ui('missing artifact id'))}</td>
              <td data-label={ui('Version')}>{String(module.version ?? 'n/a')}</td>
              <td data-label={ui('Enabled')}>{ui(module.enabled === false ? 'Disabled' : module.enabled === true ? 'Enabled' : 'Unknown')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RuntimeHistory({ rows }: { rows: RuntimeHistoryRow[] }) {
  const ui = useVisibleCopy();

  if (!rows.length) return <div className="empty-state"><strong>{ui('No history entries returned.')}</strong><p>{ui('The session API returned no history rows for this session.')}</p></div>;
  return (
    <div className="record-list">
      {rows.map((row, index) => (
        <div className="record-card" key={`${row.timestamp}:${row.role}:${index}`}>
          <strong>{row.role}</strong>
          <p>{row.contentPreview}</p>
          <small>{row.timestamp}</small>
        </div>
      ))}
    </div>
  );
}

function PromptArtifactTable({ rows }: { rows: RuntimePromptArtifactRow[] }) {
  const ui = useVisibleCopy();

  if (!rows.length) return <div className="empty-state"><strong>{ui('No prompt artifact refs returned.')}</strong><p>{ui('Runtime prompt evidence remains unavailable until the API provides hash/artifact refs.')}</p></div>;
  return (
    <div className="table-scroll">
      <table className="data-table runtime-table">
        <thead>
          <tr><th>{ui('Name')}</th><th>{ui('Artifact')}</th><th>{ui('Version')}</th><th>{ui('Hash')}</th></tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.artifactId}:${row.version}:${index}`}>
              <td data-label={ui('Name')}>{row.name || ui('unnamed')}</td>
              <td data-label={ui('Artifact')}>{row.artifactId || ui('missing artifact id')}</td>
              <td data-label={ui('Version')}>{row.version || 'n/a'}</td>
              <td data-label={ui('Hash')}>{row.hash || ui('missing hash')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditDetail({ event }: { event: RuntimeAuditRow }) {
  const ui = useVisibleCopy();

  return (
    <section className="panel">
      <div className="panel-header"><h2>{ui('Audit event')}</h2></div>
      <PropertyTable rows={[
        ['ID', event.id],
        ['Timestamp', event.timestamp || 'unknown'],
        ['Actor', event.actor],
        ['Action', event.action],
        ['Resource kind', event.resourceKind || 'unknown'],
        ['Resource id', event.resourceId || 'unknown'],
      ]} />
    </section>
  );
}

function StateBadge({ tone, label }: { tone: RuntimeStatusTone; label: string }) {
  const ui = useVisibleCopy();
  return <span className={`state-badge tone-${tone}`}>{ui(label)}</span>;
}

function ContractGaps({ gaps, compact = false }: { gaps: string[]; compact?: boolean }) {
  const ui = useVisibleCopy();

  return (
    <section className={compact ? 'notice notice-warning' : 'panel'}>
      <div className={compact ? undefined : 'panel-header'}>
        <div>
          <h2>{ui('Backend Contract Gaps')}</h2>
          {!compact ? <p>{ui('Unsupported or partially supported backend capabilities are explicit instead of hidden.')}</p> : null}
        </div>
      </div>
      <ul className="gap-list">{gaps.map(gap => <li key={gap}>{ui(gap)}</li>)}</ul>
    </section>
  );
}

function RuntimeNotice({ loading, notices }: { loading: boolean; notices: string[] }) {
  const ui = useVisibleCopy();

  if (loading) return <div className="notice">{ui('Loading live runtime evidence…')}</div>;
  if (!notices.length) return null;
  return (
    <div className="notice notice-warning">
      <strong>{ui('Partial live data unavailable.')}</strong>
      <ul>{notices.map(notice => <li key={notice}>{ui(notice)}</li>)}</ul>
    </div>
  );
}

function EmptyState({ state }: { state: { title: string; description: string } }) {
  const ui = useVisibleCopy();
  return <div className="empty-state"><strong>{ui(state.title)}</strong><p>{ui(state.description)}</p></div>;
}

function runtimeActionLabel(locale: LocaleCode, key: RuntimeActionKey) {
  const labels: Record<LocaleCode, Record<RuntimeActionKey, string>> = {
    'en-US': {
      auditHistory: 'Audit History',
      runtimeOperations: 'Runtime Operations',
      runtimeProfile: 'Runtime Profile',
      sessionDetail: 'Session Detail',
      runtimeAudit: 'Runtime Audit',
    },
    'zh-CN': {
      auditHistory: '审计历史',
      runtimeOperations: '运行时操作',
      runtimeProfile: '运行时配置档',
      sessionDetail: '会话详情',
      runtimeAudit: '运行时审计',
    },
  };
  return labels[locale][key];
}

function sessionStatus(session: RuntimeSessionRow) {
  return [
    { label: 'Runtime profile', value: session.runtimeProfileId || 'Missing', evidence: 'SessionRuntimeProfile.id', tone: session.runtimeProfileId ? 'blue' as const : 'yellow' as const },
    { label: 'Status', value: session.runtimeStatus, evidence: 'current / behind / missing', tone: session.runtimeStatus === 'current' ? 'green' as const : session.runtimeStatus === 'behind' ? 'yellow' as const : 'muted' as const },
    { label: 'Thread', value: session.threadId || 'Unavailable', evidence: 'codex_thread_id / agent_session_id', tone: session.threadId ? 'green' as const : 'muted' as const },
    // Yellow means "something should be here and is not". For an adapter with
    // no developer-instructions channel nothing should be here, so the tile
    // reports the channel instead of flagging a gap that does not exist.
    { label: 'Prompt refs', value: String(session.promptArtifactCount), evidence: session.developerInstructionsHash || session.developerPromptArtifact || session.promptEvidenceNote || 'hash/ref missing', tone: session.developerInstructionsHash || session.developerPromptArtifact || session.promptEvidenceNote ? 'green' as const : 'yellow' as const },
  ];
}

async function capture(key: string, promise: Promise<unknown>, errors: Record<string, string>) {
  try {
    return await promise;
  } catch (error) {
    errors[key] = error instanceof Error ? error.message : String(error);
    return undefined;
  }
}

async function captureOptional(promise: Promise<unknown>) {
  try {
    return await promise;
  } catch {
    return undefined;
  }
}

function getEngagementSessions(value: unknown): RuntimeSessionRow[] {
  const sessions = (value && typeof value === 'object' && !Array.isArray(value) ? (value as { sessions?: unknown }).sessions : undefined);
  return Array.isArray(sessions) ? sessions.map(session => buildRuntimeSessionDetailModel({ session }).session) : [];
}

function normalizeRuntimeTab(value: string | null | undefined): RuntimeTab {
  return value === 'sessions' || value === 'audit' || value === 'gaps' ? value : 'engagements';
}

function normalizeEngagementTab(value: string | null | undefined): EngagementTab {
  return value === 'timeline' || value === 'stream' || value === 'controls' || value === 'exports' || value === 'test-message' || value === 'raw' ? value : 'overview';
}

function normalizeSessionTab(value: string | null | undefined): SessionTab {
  return value === 'history' || value === 'raw' ? value : 'overview';
}

function normalizeProfileTab(value: string | null | undefined): ProfileTab {
  return value === 'prompts' || value === 'raw' ? value : 'overview';
}

function normalizeAuditTab(value: string | null | undefined): AuditTab {
  return value === 'raw' ? 'raw' : 'overview';
}

export const runtimeEvidenceContractGaps = runtimeContractGaps;
